import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const StatusUpdatePanel = ({ batch, onStatusUpdate, onBack }) => {
  const [selectedStatus, setSelectedStatus] = useState(batch?.status || '');
  const [operatorId, setOperatorId] = useState('');
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState('');

  const statusOptions = [
    { value: 'pending', label: 'En attente', icon: 'Clock', color: 'bg-gray-100 text-gray-700' },
    { value: 'in_progress', label: 'En cours', icon: 'Play', color: 'bg-primary/10 text-primary' },
    { value: 'quality_check', label: 'Contrôle qualité', icon: 'CheckCircle', color: 'bg-warning/10 text-warning' },
    { value: 'completed', label: 'Terminé', icon: 'Check', color: 'bg-success/10 text-success' },
    { value: 'delayed', label: 'Retardé', icon: 'AlertCircle', color: 'bg-error/10 text-error' }
  ];

  const phaseOptions = [
    'Réception',
    'Préparation',
    'Fabrication',
    'Contrôle qualité',
    'Finition',
    'Emballage',
    'Expédition'
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!operatorId?.trim()) {
      alert('Veuillez saisir votre identifiant opérateur');
      return;
    }

    const updateData = {
      status: selectedStatus,
      operatorId,
      phase,
      notes,
      timestamp: new Date()?.toISOString()
    };

    onStatusUpdate(batch?.id, selectedStatus, operatorId, notes);
  };

  if (!batch) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        <Icon name="AlertCircle" size={64} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun lot sélectionné</h3>
        <p className="text-sm text-gray-600 mb-4">Scannez un QR code ou sélectionnez un lot</p>
        <Button variant="outline" iconName="ArrowLeft" onClick={onBack}>
          Retour au suivi
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Batch Information */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-6">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" iconName="ArrowLeft" onClick={onBack} />
            <h3 className="text-lg font-semibold text-gray-900">Informations du lot</h3>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Numéro de lot</div>
              <div className="text-base font-semibold text-gray-900">{batch?.batchNumber}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Patient</div>
              <div className="text-base text-gray-900">{batch?.patientName}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Clinique</div>
              <div className="text-base text-gray-900">{batch?.clinicName}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Échéance</div>
              <div className="text-base text-gray-900">
                {new Date(batch?.deadline)?.toLocaleDateString('fr-FR')}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Statut actuel</div>
              <div className="text-base font-semibold text-gray-900 capitalize">
                {batch?.status?.replace('_', ' ')}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">Progression</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2"
                    style={{ width: `${batch?.progress}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-900">{batch?.progress}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Status Update Form */}
      <div className="lg:col-span-2">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Mise à jour du statut</h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Status Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">Nouveau statut</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {statusOptions?.map(option => (
                  <button
                    key={option?.value}
                    type="button"
                    onClick={() => setSelectedStatus(option?.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedStatus === option?.value
                        ? 'border-primary bg-primary/5' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon name={option?.icon} size={24} className="mx-auto mb-2" />
                    <div className="text-sm font-medium text-gray-900">{option?.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Phase Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Phase de production</label>
              <select
                value={phase}
                onChange={(e) => setPhase(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                <option value="">Sélectionner une phase</option>
                {phaseOptions?.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Operator ID */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Identifiant opérateur *</label>
              <Input
                type="text"
                placeholder="Ex: OP-001"
                value={operatorId}
                onChange={(e) => setOperatorId(e?.target?.value)}
                required
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Notes (optionnel)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                placeholder="Ajouter des observations..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Timestamp Display */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Icon name="Clock" size={16} />
                <span>Horodatage: {new Date()?.toLocaleString('fr-FR')}</span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                iconName="X"
                onClick={onBack}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                iconName="Check"
                className="flex-1"
                disabled={!selectedStatus || !operatorId?.trim()}
              >
                Enregistrer la mise à jour
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdatePanel;